import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { font, Theme } from '../../utils/themes';
import { formatLabel, hexToRgba, formatDateDefault } from '../../utils/helper';
import StyledText from '../StyledComponents/StyledText';
import { useGetPublicDirectoryQuery } from '../../api/pageBuilderApiSlice';
import SearchBar from '../searchBar/SearchBar';
import Avatar from '../Avatar/Avatar';
import { SkeletonCell } from '../SkeletonCell';
import Pagination from '../listing/Pagination';
import { Button } from '../form/Button';
import ViewIcon from '../../assets/icons/ViewIcon';
import ContactDetailsModal from './ContactDetailsModal';

// TypeScript Interfaces
export interface ContactInfo {
    id: number | string;
    [key: string]: any;
}

export interface ContactDisplayField {
    label: string;
    value: string;
}

export interface ContactFields {
    workPhone: string;
    workEmail: string;
    workAddress: string;
    email: string;
    phone: string;
    address: string;
}

/**
 * PUBLIC DIRECTORY PAYLOAD FLOW
 * 
 * 1. EDIT MODE (This component):
 *    - User configures Public Directory in Properties Panel
 *    - PublicDirectoryProperties builds: { selectedColumns, criteria, scope }
 *    - This payload is stored in tempSettings.publicDirectoryPayload
 *    - InnerElement passes it to PublicDirectory for preview
 *    - Used for filtering what to display in edit mode
 * 
 * 2. SAVE/PUBLISH:
 *    - handleApplySettings() stores full tempSettings in pendingChanges
 *    - applyPendingChangesToCanvas() merges into element properties
 *    - element.properties now contains: { ..., publicDirectoryPayload }
 * 
 * 3. BACKEND SUBMISSION (Page Publish):
 *    - generatePagePayload() creates final payload for backend
 *    - Element properties (including publicDirectoryPayload) are included
 *    - Sent to: POST /page-builder/page/:id/publish
 *    - Backend receives:
 *      {
 *        rows: [
 *          {
 *            elements: [
 *              {
 *                type: 'public_directory',
 *                properties: {
 *                  scope: 'contact',
 *                  publicDirectoryPayload: {
 *                    selectedColumns: ['c.full_name', 'c.email', ...],
 *                    criteria: {
 *                      logic: 'AND',
 *                      conditions: [...]
 *                    }
 *                  }
 *                }
 *              }
 *            ]
 *          }
 *        ]
 *      }
 * 
 * 4. FRONTEND RENDERING (Live Page):
 *    - Frontend fetches element properties with publicDirectoryPayload
 *    - Uses selectedColumns to decide which fields to show
 *    - Uses criteria to filter contacts/organizations
 *    - API call: POST /page-directory with payload = publicDirectoryPayload
 */

const DIRECTORY_ITEMS_PER_PAGE = 10;

// Sample Data




// Styled Components
const Container = styled.div<{ $compact?: boolean, theme: Theme }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ $compact }) => ($compact ? '0' : '40px 20px')};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  background: ${({ theme }) => theme.CorporateTheme.colors.filterBG};
  
  @media (max-width: 1500px) {
    padding: ${({ $compact }) => ($compact ? '0' : '30px 16px')};
  }
  
  @media (max-width: 640px) {
    padding: ${({ $compact }) => ($compact ? '0' : '20px 12px')};
    max-width: 100%;
  }
`;

const Grid = styled.div<{ $singleColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $singleColumn }) => ($singleColumn ? '1fr' : 'repeat(2, 1fr)')};
  gap: 15px;
  
  @media (max-width: 1500px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  @media (max-width: 640px) {
    gap: 10px;
  }
`;

const EmptyState = styled.div<{ theme: Theme }>`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 8px;
  padding: 40px 20px;
`;

const EmptyStateText = styled.div`
  font-size: 16px;
  color: #718096;
  font-weight: 500;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 1500px) {
    gap: 10px;
  }
  
  @media (max-width: 640px) {
    gap: 8px;
    width: 100%;
  }
`;

const Card = styled.div<{ theme: Theme; $fullWidth?: boolean }>`

   background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    width: 100%;
    max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'none')};
 
`;

const CardHeader = styled.div<{ theme: Theme, $compact?: boolean }>`
  background: ${({ theme }) => theme.CorporateTheme.colors.lightBlueHeaderBg};
  padding: ${({ $compact }) => ($compact ? '12px 14px' : '18px 22px')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $compact }) => ($compact ? '10px' : '16px')};
  flex-wrap: wrap;
  
  @media (max-width: 1500px) {
    padding: ${({ $compact }) => ($compact ? '10px 12px' : '14px 16px')};
    gap: ${({ $compact }) => ($compact ? '8px' : '12px')};
  }
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    padding: ${({ $compact }) => ($compact ? '8px 10px' : '12px 14px')};
    gap: 8px;
  }
  
  > div:first-child {
    @media (max-width: 640px) {
      width: 100%;
    }
  }
`;



const HeaderInfo = styled.div`
  flex: 1;
  text-align: left;
  min-width: 0;
  
  @media (max-width: 640px) {
    width: 100%;
  }
`;

const BulletDot = styled.div<{ theme: Theme }>`
  height:6px;
  width:6px;
  background-color:${({ theme }) => theme.CorporateTheme.colors.count};
  border-radius:50%;
`;

const Name = styled.h3`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
`;

const Email = styled.p`
  margin: 0;
  font-size: 14px;
  color: #718096;
`;

const CardBody = styled.div<{ theme: Theme, $compact?: boolean }>`
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  padding: ${({ $compact }) => ($compact ? '12px 14px' : '24px')};
  
  @media (max-width: 1500px) {
    padding: ${({ $compact }) => ($compact ? '10px 12px' : '16px')};
  }
  
  @media (max-width: 640px) {
    padding: ${({ $compact }) => ($compact ? '8px 10px' : '12px')};
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 1500px) {
    gap: 10px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 640px) {
    gap: 8px;
    margin-bottom: 10px;
  }
`;

const IconWrapper = styled.div<{ theme: Theme, $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $compact }) => ($compact ? '24px' : '30px')};
  height: ${({ $compact }) => ($compact ? '24px' : '30px')};
  background-color: ${({ theme }) => theme.CorporateTheme.colors.builderBg};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius:32px;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
display: flex;
align-items: center;
gap: 4px;

`;

const InfoLabel = styled.div`
  font-size: 13px;
  color: #718096;
  margin-bottom: 4px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #2d3748;
  line-height: 1.5;
  word-break: break-word;
`;

// Contact Card Component
interface ContactCardProps {
    scope: string;
    contact: ContactInfo;
    compact?: boolean;
    isResponsive?: boolean;
    fullWidth?: boolean;
    isPreview?: boolean;
    onViewAllFields?: (contact: ContactInfo) => void;
}

interface PublicDirectoryProps {
    scope: string;
    singleCardOnly?: boolean;
    compactPreview?: boolean;
    showPagination?: boolean;
    selectedColumns?: string[];
    criteria?: {
        logic: 'AND' | 'OR';
        conditions: Array<{
            field: string;
            operator: string;
            value: any;
        }>;
    };
    queryPayload?: {
        selectedColumns?: string[];
        criteria?: {
            logic: 'AND' | 'OR';
            conditions: Array<{
                field: string;
                operator: string;
                value: any;
            }>;
        };
    };
}

type ContactPresentation = {
    displayName: string;
    displayEmail: string;
    displayImageUrl: string;
    visibleFields: ContactDisplayField[];
    overflowFields: ContactDisplayField[];
};

function getDisplayString(value: any): string {
    if (value === null) return '-';
    if (value === undefined || value === '') return '-';

    // Check if value is a Date object or a date string
    if (value instanceof Date && !isNaN(value.getTime())) {
        return formatDateDefault(value);
    }

    // Check if it's a string that looks like a date (ISO 8601 or date-like format)
    if (typeof value === 'string' && value.trim()) {
        const trimmed = value.trim();
        // Match ISO date format (YYYY-MM-DD...) or date patterns like "2024-01-15T..."
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || /\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmed)) {
            const date = new Date(trimmed);
            if (!isNaN(date.getTime())) {
                return formatDateDefault(date);
            }
        }
    }

    if (Array.isArray(value)) {
        return value
            .map((item: any): string => getDisplayString(item))
            .filter((item) => item !== '-')
            .join(', ') || '-';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

const buildContactPresentation = (contact: ContactInfo): ContactPresentation => {
    const rawRecord = contact || {};
    const nestedFields = rawRecord.fields && typeof rawRecord.fields === 'object' && !Array.isArray(rawRecord.fields)
        ? rawRecord.fields
        : {};

    const displayName = getDisplayString(
        rawRecord.contactName
        ?? rawRecord.fullName
        ?? rawRecord.name
        ?? rawRecord['Full Name']
        ?? [rawRecord.firstName ?? rawRecord['First Name'], rawRecord.lastName ?? rawRecord['Last Name']]
            .filter(Boolean)
            .join(' ')
            .trim()
    );

    const displayEmail = getDisplayString(
        rawRecord.contactEmail
        ?? rawRecord.email
        ?? nestedFields.workEmail
        ?? nestedFields.email
        ?? rawRecord['Email']
    );

    const displayImageUrl = String(rawRecord.contactSignedUrl ?? rawRecord.signedUrl ?? '');

    const flattenedEntries: ContactDisplayField[] = [];
    const pushedKeys = new Set<string>();
    const excludedKeys = new Set(['contactname', 'contactemail', 'contactsignedurl', 'id', 'contact_id', 'contactid']);

    const pushField = (label: string, value: any, key: string) => {
        const normalizedKey = key.toLowerCase();
        if (excludedKeys.has(normalizedKey) || pushedKeys.has(`${normalizedKey}:${label.toLowerCase()}`)) {
            return;
        }

        const displayValue = getDisplayString(value);
        if (value === undefined) {
            return;
        }

        pushedKeys.add(`${normalizedKey}:${label.toLowerCase()}`);
        flattenedEntries.push({ label, value: displayValue });
    };

    Object.entries(rawRecord).forEach(([key, value]) => {
        if (key === 'fields' && value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                pushField(formatLabel(nestedKey), nestedValue, nestedKey);
            });
            return;
        }

        pushField(formatLabel(key), value, key);
    });

    const visibleFields = flattenedEntries.slice(0, 4);
    const overflowFields = flattenedEntries.slice(4);

    return {
        displayName,
        displayEmail,
        displayImageUrl,
        visibleFields,
        overflowFields,
    };
};

const buildOrganizationPresentation = (organization: ContactInfo): ContactPresentation => {
    const rawRecord = organization || {};
    const nestedFields = rawRecord.fields && typeof rawRecord.fields === 'object' && !Array.isArray(rawRecord.fields)
        ? rawRecord.fields
        : {};

    const displayName = getDisplayString(
        rawRecord.organizationName
        ?? rawRecord.orgName
        ?? rawRecord.name
        ?? rawRecord['Organization Name']
    );

    const displayEmail = getDisplayString(
        rawRecord.organizationEmail
        ?? rawRecord.orgEmail
        ?? rawRecord.email
        ?? nestedFields.email
        ?? rawRecord['Email']
    );

    const displayImageUrl = String(rawRecord.organizationSignedUrl ?? rawRecord.signedUrl ?? '');

    const flattenedEntries: ContactDisplayField[] = [];
    const pushedKeys = new Set<string>();
    const excludedKeys = new Set(['organizationname', 'orgname', 'organizationemail', 'orgemail', 'organizationsignedurl', 'id', 'organization_id', 'organizationid']);

    const pushField = (label: string, value: any, key: string) => {
        const normalizedKey = key.toLowerCase();
        if (excludedKeys.has(normalizedKey) || pushedKeys.has(`${normalizedKey}:${label.toLowerCase()}`)) {
            return;
        }

        const displayValue = getDisplayString(value);
        if (value === undefined) {
            return;
        }

        pushedKeys.add(`${normalizedKey}:${label.toLowerCase()}`);
        flattenedEntries.push({ label, value: displayValue });
    };

    Object.entries(rawRecord).forEach(([key, value]) => {
        if (key === 'fields' && value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                pushField(formatLabel(nestedKey), nestedValue, nestedKey);
            });
            return;
        }

        pushField(formatLabel(key), value, key);
    });

    const visibleFields = flattenedEntries.slice(0, 4);
    const overflowFields = flattenedEntries.slice(4);

    return {
        displayName,
        displayEmail,
        displayImageUrl,
        visibleFields,
        overflowFields,
    };
};

const ContactCard: React.FC<ContactCardProps> = ({ scope, contact, compact = false, isResponsive = false, fullWidth = false, isPreview = false, onViewAllFields }) => {
    const { theme } = useTheme();
    const presentation = scope === 'organization' ? buildOrganizationPresentation(contact) : buildContactPresentation(contact);
    const isMobileSize = isResponsive || (typeof window !== 'undefined' && window.innerWidth <= 640);
    
    return (
        <Card theme={theme} $fullWidth={fullWidth}>
            <CardHeader theme={theme} $compact={compact}>
                <HeaderContent>
                    <div>
                        <Avatar 
                          imageUrl={presentation.displayImageUrl} 
                          name={presentation.displayName} 
                          size={compact ? 16 : (isMobileSize ? 40 : 59)} 
                        />
                    </div>
                    <HeaderInfo>
                        <StyledText 
                          textColor={theme.CorporateTheme.colors.primary} 
                          fontSize={compact ? '10px' : (isMobileSize ? '12px' : '15px')} 
                          fontWeight={700}
                        >
                          {presentation.displayName}
                        </StyledText>
                        <StyledText 
                          textColor={theme.CorporateTheme.colors.textSecondary} 
                          fontSize={compact ? '9px' : (isMobileSize ? '10px' : '12px')} 
                          fontWeight={400}
                        >
                          {presentation.displayEmail}
                        </StyledText>
                    </HeaderInfo>
                </HeaderContent>
                {presentation.overflowFields.length > 0 && (
                    <Button
                        theme={theme}
                        btnBg={theme?.CorporateTheme?.colors?.background}
                        color={theme?.CorporateTheme?.colors?.textSecondary}
                        type="button"
                        $variant="customButton"
                        className="px-1 gap-1"
                        borderColor={theme?.CreateFormTheme?.colors?.border}
                        fontSize={isMobileSize ? '11px' : '13px'}
                        onClick={!isPreview ? () => onViewAllFields?.(contact) : undefined}
                        disabled={isPreview}
                    >
                        <ViewIcon color={theme.CorporateTheme.colors.gunmetal}/>
                        View All Fields
                    </Button>
                )}
            </CardHeader>
            <CardBody theme={theme} $compact={compact}>
                {presentation.visibleFields.map((field) => (
                    <InfoRow key={`${field.label}-${field.value}`}>
                        <div><BulletDot theme={theme} /></div>
                        <InfoContent>
                            <StyledText style={{textWrap:'nowrap'}} textColor={theme.CorporateTheme.colors.textSecondary} fontSize={compact ? '8px' : '13px'} fontWeight={600}>
                                {field.label}:
                            </StyledText>
                            <StyledText textColor={theme.CorporateTheme.colors.headerColor} fontSize={compact ? '7px' : '14px'} fontWeight={600}>
                                {field.value}
                            </StyledText>
                        </InfoContent>
                    </InfoRow>
                ))}
            </CardBody>
        </Card>
    );
};

// Main Component
const PublicDirectory: React.FC<PublicDirectoryProps> = ({
    scope,
    singleCardOnly = false,
    compactPreview = false,
    showPagination = false,
    selectedColumns = [],
    criteria,
    queryPayload,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DIRECTORY_ITEMS_PER_PAGE);
    const [selectedContact, setSelectedContact] = useState<ContactInfo | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const resolvedSelectedColumns = queryPayload?.selectedColumns ?? selectedColumns;
    const resolvedCriteria = queryPayload?.criteria ?? criteria;
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const [isResponsive, setIsResponsive] = useState(false);
    


    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleViewAllFields = (contact: ContactInfo) => {
        setSelectedContact(contact);
        setIsModalOpen(true);
    };

    const scrollToTop = () => {
        tabsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const selectedContactPresentation = selectedContact ? (scope === 'organization' ? buildOrganizationPresentation(selectedContact) : buildContactPresentation(selectedContact)) : null;

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedContact(null);
    };

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [searchTerm]);

    const { data, isLoading, isFetching, refetch } = useGetPublicDirectoryQuery({
        name: 'public directory',
        scope,
        query: debouncedSearchTerm || undefined,
        page: singleCardOnly ? undefined : (showPagination ? currentPage : 1),
        perPage: singleCardOnly ? undefined : itemsPerPage,
        queryPayload: queryPayload,
    }, { refetchOnMountOrArgChange: true });

    const { theme } = useTheme();

    // Refetch when queryPayload changes (e.g., when Save is clicked in properties panel)
    useEffect(() => {
        if (queryPayload && (queryPayload.selectedColumns?.length || queryPayload.criteria?.conditions?.length)) {
            refetch();
        }
    }, [queryPayload, refetch]);

    const records: ContactInfo[] = data?.data?.results || [];
    const visibleRecords = singleCardOnly ? records.slice(0, 1) : records;
    const hasData = visibleRecords.length > 0;
    const isCompactMode = singleCardOnly || compactPreview;

    const serverTotalItems = data?.data?.total ?? data?.total;
    const totalItems = typeof serverTotalItems === 'number' ? serverTotalItems : visibleRecords.length;
    const serverTotalPages = data?.data?.totalPages ?? data?.totalPages;
    const totalPages = typeof serverTotalPages === 'number'
        ? serverTotalPages
        : Math.ceil(totalItems / itemsPerPage) || 0;

    const hasServerPaginationMeta =
        typeof serverTotalItems === 'number' || typeof serverTotalPages === 'number';

    const paginatedRecords = showPagination && !singleCardOnly && !hasServerPaginationMeta
        ? visibleRecords.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        : visibleRecords;

    const isSingleCardView = singleCardOnly || isResponsive;

    useEffect(() => {
        setCurrentPage(1);
    }, [scope, debouncedSearchTerm]);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (!showPagination || singleCardOnly) return;
        tabsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [currentPage, itemsPerPage, showPagination, singleCardOnly]);


    useEffect(() => {
        const container = tabsContainerRef.current;
        if (!container) return;

        // Create ResizeObserver
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const width = entry.contentRect.width;
                if (width < 690) {
                    setIsResponsive(true);
                } else {
                    setIsResponsive(false);
                }
            }
        });

        // Observe the container
        resizeObserver.observe(container);

        // Cleanup
        return () => {
            resizeObserver.unobserve(container);
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Container $compact={isCompactMode} theme={theme} ref={tabsContainerRef}>
            {!singleCardOnly && (
                <SearchBar
                    placeholder={scope === 'contact' ? "Search Contacts..." : "Search Organization..."}
                    style={{ width: '100%', marginBottom: isCompactMode ? 12 : 20, background: theme.CorporateTheme.colors.background }}
                    onSearchChange={handleSearchChange}
                    searchTerm={searchTerm}
                    disabled={compactPreview}

                />
            )}
            <Grid $singleColumn={isSingleCardView}>
                {isLoading || isFetching ? (
                    Array.from({ length: isSingleCardView ? 1 : 4 }).map((_, index) => (
                        <SkeletonCell
                            key={index}
                            height="200px"
                            width="100%"
                            style={{ borderRadius: 8 }}
                        />
                    ))
                ) : hasData ? (
                    (singleCardOnly ? paginatedRecords.slice(0, 1) : paginatedRecords).map((contact: ContactInfo) => (
                        <ContactCard
                            key={contact.id}
                            contact={contact}
                            compact={isCompactMode}
                            scope={scope}
                            isResponsive={isResponsive}
                            fullWidth={singleCardOnly && isSingleCardView}
                            isPreview={compactPreview}
                            onViewAllFields={handleViewAllFields}
                        />
                    ))
                ) : (
                    <EmptyState theme={theme}>
                        <EmptyStateText>
                            {searchTerm
                                ? `No matching ${scope === 'contact' ? 'contacts' : 'organizations'} found`
                                : `No ${scope === 'contact' ? 'contacts' : 'organizations'} found`}
                        </EmptyStateText>
                    </EmptyState>
                )}
            </Grid>

            {showPagination && !singleCardOnly && hasData && (
                <Pagination
                    className="mt-3 py-1"
                    currentPage={currentPage}
                    totalItems={totalItems}
                    perPage={itemsPerPage}
                    totalPages={totalPages}
                    onPageChange={(page: number) => {
                        if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                        }
                    }}
                    onLimitChange={(perPage: number) => {
                        setItemsPerPage(perPage);
                        setCurrentPage(1);
                    }}
                />
            )}

            <ContactDetailsModal
                contact={selectedContact}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                displayName={selectedContactPresentation?.displayName}
                displayEmail={selectedContactPresentation?.displayEmail}
                displayImageUrl={selectedContactPresentation?.displayImageUrl}
                fields={selectedContactPresentation ? [...selectedContactPresentation.visibleFields, ...selectedContactPresentation.overflowFields] : []}
            />
        </Container>
    );
};

export default PublicDirectory;